import { useEffect, useRef, useState } from 'react';

import { onError } from '../../../utils/error.util';
import { getApplication } from '../../../services/applications.service';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../../utils/content.util';
import { Application } from '../../../models/application.model';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import { Col, Row } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { Button } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { formatMoney } from '../../../utils/format.util';
import { Dealer } from '../../../models/dealer.model';
import { getDealer } from '../../../services/dealers.service';

export const formatSSN = (ssn?: string): string => {
  if (!ssn) return '';
  if (ssn.length === 9) {
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
  }
  return ssn;
};

export function formatUTCDate(dateString: string | undefined, formatString: string = 'MM/dd/yyyy'): string {
  if (!dateString) return '';

  try {
    const parsedDate = parseISO(dateString);
    const utcDate = toZonedTime(parsedDate, 'UTC');
    return format(utcDate, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

export default function ApplicationFormController() {
  const { isAuthenticated } = useAppContext();
  const navigate = useNavigate();

  const contentToPrint = useRef<any>(null);

  const handlePrint = useReactToPrint({ contentRef: contentToPrint });

  const { applicationId } = useParams();
  const [application, setApplication] = useState<Application | undefined>(undefined);
  const [dealer, setDealer] = useState<Dealer | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  async function onLoad() {
    if (!isAuthenticated || !applicationId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await getApplication(applicationId);
      setApplication(response);

      if (response?.dealerId) {
        const dealerResponse = await getDealer(response.dealerId);
        setDealer(dealerResponse);
      }
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderApplication() {
    return (
      <div>
        <Row className="my-3">
          <Col>
            <Button onClick={() => handlePrint()} variant="outlined" startIcon={<PrintIcon />} className="me-2">
              Print
            </Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Col>
        </Row>

        <div id="pdfview">
          <div className="application-form-print" id={`application-${applicationId}`} ref={contentToPrint}>
            <section className="pdfcol-full">
              <img alt="logo" id="print-logo" src="/assets/images/ccl-logo.png" />
              <p id="pdfdate">
                <small>
                  <strong>Date:</strong>{' '}
                  {application?.createdAt ? formatUTCDate(application?.createdAt.toISOString()) : ''}
                  {application?.createdBy ? (
                    <>
                      {' | '}
                      <strong>Created By:</strong> {application.createdBy}
                    </>
                  ) : null}
                </small>
              </p>
            </section>

            <section className="pdfcol">
              <h6>Sale Details</h6>
              <p>
                <strong>Seller Type:</strong> {application?.sellerType}
                <br />
                {application?.dealerName ? (
                  <>
                    <strong>Dealer Name:</strong> {application?.dealerName}
                    {dealer?.address ? (
                      <>
                        <br />
                        {dealer?.address.addressLine1}
                        <br />
                        {dealer?.address.city}, {dealer?.address.state} {dealer?.address.zipCode}
                      </>
                    ) : undefined}
                  </>
                ) : undefined}
                {application?.sellerName ? (
                  <>
                    <strong>Seller Name:</strong> {application?.sellerName}
                  </>
                ) : undefined}
              </p>
              <p>
                {application?.sellerContact ? (
                  <>
                    <strong>Salesperson/Contact:</strong> {application?.sellerContact}
                    <br />
                  </>
                ) : undefined}
                {application?.sellerPhoneNumber ? (
                  <>
                    <strong>p:</strong> {application?.sellerPhoneNumber}
                  </>
                ) : undefined}
              </p>
              <p>
                <strong>Daily Driver:</strong> {application?.forDailyDriver ? 'Yes' : 'No'}
              </p>

              {application?.dealerStock ? (
                <>
                  <p>
                    <strong>Stock #:</strong> {application?.dealerStock}
                    <br />
                    <strong>Location:</strong> {application?.dealerLocation}
                  </p>
                </>
              ) : undefined}

              <p>
                <strong>Year:</strong> {application?.vehicleYear}
                <br />
                <strong>Make:</strong> {application?.vehicleMake}
                <br />
                <strong>Model:</strong> {application?.vehicleModel}
              </p>
              {application?.collateralType || application?.unitStatus ? (
                <p>
                  <strong>Collateral Type:</strong> {application?.collateralType}
                  <br />
                  <strong>Unit Status:</strong> {application?.unitStatus}
                </p>
              ) : undefined}
              <p>
                <strong>Sales Price:</strong> {formatMoney(application?.vehicleSalesPrice)}
                <br />
                <strong>Down Payment:</strong> {formatMoney(application?.vehicleDownPayment)}
              </p>

              {application?.notes ? (
                <>
                  <h6>Additional Notes</h6>
                  <p>{application.notes}</p>
                </>
              ) : undefined}
            </section>

            <section className="pdfcol">
              <h6>Buyer Information</h6>
              <p>
                {application?.firstName} {application?.middleName} {application?.lastName} {application?.suffix}
                <br />
                <strong>p:</strong> {application?.primaryPhoneNumber} ({application?.primaryPhoneType})
                <br />
                <strong>e:</strong> {application?.emailAddress}
                <br />
                <br />
                <strong>SSN:</strong> {formatSSN(application?.ssn)}
                <br />
                <strong>DOB:</strong> {formatUTCDate(application?.birthdate)}
                <br />
                <strong>Marital Status:</strong> {application?.maritalStatus}
              </p>

              <p>
                <strong>Current Address:</strong>
                <br />
                {application?.residentialAddressLine1} {application?.residentialAddressLine2}
                <br />
                {application?.residentialCity}, {application?.residentialState} {application?.residentialZipCode}
                <br />
                {application?.yearsAtResidentialAddress} Years, {application?.monthsAtResidentialAddress} Months
                <br />
                <strong>Living Status:</strong> {application?.statusAtResidentialAddress}
                <br />
                <strong>Monthly Payment:</strong> {formatMoney(application?.monthlyPayment)}
              </p>

              {application?.previousAddressLine1 ? (
                <>
                  <p>
                    <strong>Previous Address:</strong>
                    <br />
                    {application?.previousAddressLine1} {application?.previousAddressLine2}
                    <br />
                    {application?.previousCity}, {application?.previousState} {application?.previousZipCode}
                    <br />
                    {application?.previousYearsAtAddress} Years, {application?.previousMonthsAtAddress} Months
                  </p>
                </>
              ) : undefined}

              <h6>Buyer Employment</h6>
              {application?.employerName ? (
                <>
                  <p>
                    <strong>Current Employer:</strong>
                    <br />
                    {application?.employerName}
                    <br />
                    {application.employerAddressLine1}
                    <br />
                    {application?.employerCity}, {application.employerState} {application.employerZipCode}
                    <br />
                    <strong>p:</strong> {application.employerPhoneNumber}
                    <br />
                    <br />
                    <strong>Title:</strong> {application.occupationTitle}
                    <br />
                    {application.employedYears} Years, {application.employedMonths} Months
                    <br />
                    <strong>Monthly Income:</strong> {formatMoney(application.grossMonthlyIncome)}
                  </p>
                </>
              ) : undefined}

              {application?.previousEmployerName ? (
                <>
                  <p>
                    <strong>Previous Employer:</strong>
                    <br />
                    {application?.previousEmployerName}
                    <br />
                    <strong>Title:</strong> {application.previousOccupationTitle}
                    <br />
                    {application.previousEmployedYears} Years, {application.previousEmployedMonths} Months
                    <br />
                    <strong>Monthly Income:</strong> {formatMoney(application.previousGrossMonthlyIncome)}
                  </p>
                </>
              ) : undefined}

              {application?.isRetired ? (
                <>
                  <p>
                    <strong>Retirement Income:</strong>
                    <br />
                    {formatMoney(application.retirementIncome)}
                    <br />
                    {application.retirementYears} years, {application.retirementMonths} months
                  </p>
                </>
              ) : undefined}

              {application?.additionalIncomeSource ? (
                <>
                  <p>
                    <strong>Adt'l Income Source:</strong>
                    <br />
                    {application.additionalIncomeSource}
                    {application?.additionalIncomeSource === 'Other' ? (
                      <>
                        <br />
                        <strong>Other:</strong> {application?.additionalIncomeSourceOther}
                      </>
                    ) : undefined}
                    <br />
                    <strong>Monthly:</strong> {formatMoney(application.additionalMonthlyIncome)}
                  </p>
                </>
              ) : undefined}
            </section>

            <section className="pdfcol">
              <h6>Co-Buyer Information</h6>
              {application?.haveCoApplicant === true ? (
                <>
                  <p>
                    {application.coapplicantFirstName} {application.coapplicantMiddleName}{' '}
                    {application.coapplicantLastName} {application.coapplicantSuffix}
                    <br />
                    <strong>p:</strong> {application?.coapplicantPrimaryPhoneNumber} (
                    {application?.coapplicantPrimaryPhoneType})
                    <br />
                    <strong>e:</strong> {application?.coapplicantEmailAddress}
                    <br />
                    <br />
                    <strong>SSN:</strong> {application.coapplicantSsn}
                    <br />
                    <strong>DOB:</strong> {formatUTCDate(application?.coapplicantBirthdate)}
                    <br />
                    <strong>Marital Status:</strong> {application.coapplicantMaritalStatus}
                  </p>

                  <p>
                    <strong>Current Address:</strong>
                    <br />
                    {application?.coapplicantResidentialAddressLine1}
                    <br />
                    {application?.coapplicantResidentialCity}, {application?.coapplicantResidentialState}{' '}
                    {application?.coapplicantResidentialZipCode}
                    <br />
                    {application?.coapplicantYearsAtResidentialAddress} Years,{' '}
                    {application?.coapplicantMonthsAtResidentialAddress} Months
                    <br />
                    <strong>Living Status:</strong> {application.coapplicantStatusAtResidentialAddress}
                    <br />
                    <strong>Monthly Payment:</strong> {formatMoney(application.coapplicantMonthlyPayment)}
                  </p>

                  {application?.coapplicantPreviousAddressLine1 ? (
                    <>
                      <p>
                        <strong>Previous Address:</strong>
                        <br />
                        {application?.coapplicantPreviousAddressLine1}
                        <br />
                        {application?.coapplicantPreviousCity}, {application?.coapplicantPreviousState}{' '}
                        {application?.coapplicantPreviousZipCode}
                        <br />
                        {application?.coapplicantPreviousYearsAtAddress} Years,{' '}
                        {application?.coapplicantPreviousMonthsAtAddress} Months
                      </p>
                    </>
                  ) : undefined}

                  <h6>Co-Buyer Employment</h6>

                  {application?.coapplicantEmployerName ? (
                    <>
                      <p>
                        <strong>Current Employer:</strong>
                        <br />
                        {application?.coapplicantEmployerName}
                        <br />
                        {application?.coapplicantEmployerAddressLine1}
                        <br />
                        {application?.coapplicantEmployerCity}, {application?.coapplicantEmployerState}{' '}
                        {application?.coapplicantEmployerZipCode}
                        <br />
                        <strong>p:</strong> {application?.coapplicantEmployerPhoneNumber}
                        <br />
                        <br />
                        <strong>Title:</strong> {application?.coapplicantOccupationTitle}
                        <br />
                        {application?.coapplicantEmployedYears} Years, {application?.coapplicantEmployedMonths} Months
                        <br />
                        <strong>Monthly Income:</strong> {formatMoney(application?.coapplicantGrossMonthlyIncome)}
                      </p>
                    </>
                  ) : undefined}

                  {application?.coapplicantPreviousEmployerName ? (
                    <>
                      <p>
                        <strong>Previous Employer:</strong>
                        <br />
                        {application?.coapplicantPreviousEmployerName}
                        <br />
                        <strong>Title:</strong> {application?.coapplicantPreviousOccupationTitle}
                        <br />
                        {application?.coapplicantPreviousEmployedYears} Years,{' '}
                        {application?.coapplicantPreviousEmployedMonths} Months
                        <br />
                        <strong>Monthly Income:</strong>{' '}
                        {formatMoney(application?.coapplicantPreviousGrossMonthlyIncome)}
                      </p>
                    </>
                  ) : undefined}

                  {application?.coapplicantIsRetired ? (
                    <>
                      <p>
                        <strong>Retirement Income:</strong>
                        <br />
                        {formatMoney(application?.coapplicantRetirementIncome)}
                        <br />
                        {application?.coapplicantRetirementYears} years, {application?.coapplicantRetirementMonths}{' '}
                        months
                      </p>
                    </>
                  ) : undefined}

                  <p>
                    {application?.coapplicantAdditionalIncomeSource ? (
                      <>
                        <strong>Adt'l Income Source:</strong>
                        <br />
                        {application?.coapplicantAdditionalIncomeSource}
                        {application?.coapplicantAdditionalIncomeSource === 'Other' ? (
                          <>
                            <br />
                            <strong>Other:</strong> {application?.coapplicantAdditionalIncomeSourceOther}
                          </>
                        ) : undefined}
                        <br />
                        <strong>Monthly Income:</strong> {formatMoney(application?.coapplicantAdditionalMonthlyIncome)}
                      </>
                    ) : undefined}
                  </p>
                </>
              ) : undefined}
            </section>

            {application?.partner ? (
              <section className="pdfcol">
                <h6>Partner Information</h6>
                <p>
                  <strong>Partner:</strong> {application.partner}
                  {application.applicationSource && application.applicationSource !== application.partner ? (
                    <>
                      {' '}
                      ({application.applicationSource})
                    </>
                  ) : undefined}
                  <br />
                  {application.partnerRep ? (
                    <>
                      <strong>Rep:</strong> {application.partnerRep}
                      <br />
                    </>
                  ) : undefined}
                  {application.partnerRepEmail ? (
                    <>
                      <strong>Rep Email:</strong> {application.partnerRepEmail}
                    </>
                  ) : undefined}
                </p>
                {application.gps || application.theft || application.tireWheel || application.vsc || application.miscCost ? (
                  <p>
                    <strong>Back-End Products:</strong>
                    <br />
                    {application.gps ? (
                      <>
                        GPS: {formatMoney(application.gps)}
                        <br />
                      </>
                    ) : undefined}
                    {application.theft ? (
                      <>
                        Theft: {formatMoney(application.theft)}
                        <br />
                      </>
                    ) : undefined}
                    {application.tireWheel ? (
                      <>
                        Tire &amp; Wheel: {formatMoney(application.tireWheel)}
                        <br />
                      </>
                    ) : undefined}
                    {application.vsc ? (
                      <>
                        VSC: {formatMoney(application.vsc)}
                        <br />
                      </>
                    ) : undefined}
                    {application.miscCost ? (
                      <>
                        {application.miscDescription || 'Misc'}: {formatMoney(application.miscCost)}
                      </>
                    ) : undefined}
                  </p>
                ) : undefined}
              </section>
            ) : undefined}

            <section className="pdfcol-full" id="pdfconsent">
              <p>
                BY CHECKING BELOW, EACH OF US (BOTH BUYER AND CO-BUYER) VERIFY OUR INTENT TO APPLY FOR JOINT CREDIT
                AUTHORIZATION. I certify that the above information stated in this application is true and correct and a
                complete statement of my financial condition. I understand that this application will be kept whether or
                not it is approved. You are authorized to share this application with other potential lenders. You and
                any potential or subsequent creditor are authorized to check my credit and my employment history to
                answer questions about your credit experience with me and to disclose credit information to each other.
                I further understand that my application is being submitted to a lender(s).
              </p>
            </section>

            <section className="pdfcol-full" id="pdfsigs">
              <div className="pdfcol-half">
                <h6>Buyer</h6>
                <p>
                  <input
                    className="me-2"
                    name="checkbox2"
                    type="checkbox"
                    id="checkbox2"
                    checked={application?.applicantAgree}
                  />
                  I agree to the Authorization
                </p>
                <p>
                  <input
                    className="me-2"
                    name="checkbox"
                    type="checkbox"
                    id="checkbox"
                    checked={application?.usCitizen}
                  />
                  I am a US Citizen
                </p>
                <p>
                  <input
                    className="me-2"
                    name="checkbox3"
                    type="checkbox"
                    id="checkbox3"
                    checked={application?.usResident}
                  />
                  I am a Permanent US Resident
                </p>
                <h5>{application?.applicantSignature}</h5>
              </div>
              <div className="pdfcol-half">
                {application?.coApplicantAgree ? (
                  <>
                    <h6>Co-Buyer</h6>
                    <p>
                      <input
                        className="me-2"
                        name="checkbox2"
                        type="checkbox"
                        id="checkbox2"
                        checked={application?.coApplicantAgree}
                      />
                      I agree to the Authorization
                    </p>
                    <p>
                      <input
                        className="me-2"
                        name="checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={application?.coApplicantUsCitizen}
                      />
                      I am a US Citizen
                    </p>
                    <p>
                      <input
                        className="me-2"
                        name="checkbox4"
                        type="checkbox"
                        id="checkbox4"
                        checked={application?.coApplicantUsResident}
                      />
                      I am a Permanent US Resident
                    </p>
                    <h5>{application?.coApplicantSignature}</h5>
                  </>
                ) : undefined}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-form">
      <Row>
        <Col>
          <div className="application">
            <h2 className="pb-2 mt-4 mb-3 border-bottom">Service Form #{applicationId}</h2>
          </div>
        </Col>
      </Row>
      {isLoading ? <CircularProgressComponent /> : renderApplication()}
    </div>
  );
}
